import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilter,
  faXmark,
  faChevronDown,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { useAxiosInstance } from "../../services/Interceptor";
import {
  fetchGraduationYears,
  fetchHighSchool,
  fetchPublicEvents,
} from "../../services/LeaderboardService";
import { positions } from "../../utils/PositionStateData";
import DropdownWithSearch from "../common/DropdownWithSearch";

const CONDITIONS = ["Select", "LessThan", "GreaterThan", "Equals"];
const LONG = "Interior Offensive Line (Guard or Center)";
const DISPLAY = "Interior Offensive Line";

const toDisplay = (p) => (p === LONG ? p : p);
const toApi = (p) => (p === DISPLAY ? p : p);

const ConditionDropdown = ({
  label,
  operator,
  value,
  showDropdown,
  onOperatorChange,
  onValueChange,
  onToggleDropdown,
  placeholder = "0",
  hasError = false,
}) => {
  const [localValue, setLocalValue] = useState(value ?? "");

  useEffect(() => {
    setLocalValue(value ?? "");
  }, [value]);

  const handleChange = (e) => {
    const newVal = e.target.value;
    setLocalValue(newVal);
    if (onValueChange) {
      onValueChange(newVal);
    }
  };

  return (
    <div className="relative w-full md:w-64">
      <label className="block mb-1 font-medium text-gray-700">{label}</label>
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative w-full sm:w-1/2">
          <button
            onClick={onToggleDropdown}
            className={`w-full flex justify-between items-center border rounded px-4 py-2 text-gray-700 bg-white ${
              hasError ? "border-red-500" : ""
            }`}
          >
            <span>{operator || "Select"}</span>
            <FontAwesomeIcon icon={faChevronDown} />
          </button>
          {showDropdown && (
            <ul className="absolute w-full mt-1 bg-white border rounded shadow z-10">
              {CONDITIONS.map((cond) => (
                <li
                  key={cond}
                  onClick={() => onOperatorChange(cond)}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                >
                  {cond}
                </li>
              ))}
            </ul>
          )}
        </div>

        <input
          type="number"
          placeholder={placeholder}
          value={localValue}
          onChange={handleChange}
          className={`border rounded px-4 py-2 text-gray-700 w-full sm:w-1/2 ${
            hasError ? "border-red-500" : ""
          }`}
          min="0"
          step="1"
        />
      </div>
    </div>
  );
};

const FilterPanel = ({
  onApplyFilters,
  filterParams,
  onClearFilters,
  includeGraduatedKids,
  setIncludeGraduatedKids,
  isHighSchoolLeaderboard = false,
  isHighSchoolCoach = false,
}) => {
  const apiInstance = useAxiosInstance();
  const [showFilters, setShowFilters] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [loading, setLoading] = useState({
    years: false,
    events: false,
    schools: false,
  });
  const [validationErrors, setValidationErrors] = useState({});

  const positionsDisplay = positions.filter((p) => p !== "None");

  const OFFER_TYPES_OPTIONS = [
    "With Any Offers",
    "With D2",
    "With D1(FBS)",
    "With D1(FCS)",
    "With D1(P4)",
    "No Offers",
    "No D2",
    "No D1(FBS)",
    "No D1(FCS)",
    "No D1(P4)",
  ];

  const [dropdownOptions, setDropdownOptions] = useState({
    year: [],
    position: positionsDisplay,
    eventIds: [],
    highSchools: [],
    states: [
      "Alabama (AL)",
      "Alaska (AK)",
      "Arizona (AZ)",
      "Arkansas (AR)",
      "California (CA)",
      "Colorado (CO)",
      "Connecticut (CT)",
      "Delaware (DE)",
      "Florida (FL)",
      "Georgia (GA)",
      "Hawaii (HI)",
      "Idaho (ID)",
      "Illinois (IL)",
      "Indiana (IN)",
      "Iowa (IA)",
      "Kansas (KS)",
      "Kentucky (KY)",
      "Louisiana (LA)",
      "Maine (ME)",
      "Maryland (MD)",
      "Massachusetts (MA)",
      "Michigan (MI)",
      "Minnesota (MN)",
      "Mississippi (MS)",
      "Missouri (MO)",
      "Montana (MT)",
      "Nebraska (NE)",
      "Nevada (NV)",
      "New Hampshire (NH)",
      "New Jersey (NJ)",
      "New Mexico (NM)",
      "New York (NY)",
      "North Carolina (NC)",
      "North Dakota (ND)",
      "Ohio (OH)",
      "Oklahoma (OK)",
      "Oregon (OR)",
      "Pennsylvania (PA)",
      "Rhode Island (RI)",
      "South Carolina (SC)",
      "South Dakota (SD)",
      "Tennessee (TN)",
      "Texas (TX)",
      "Utah (UT)",
      "Vermont (VT)",
      "Virginia (VA)",
      "Washington (WA)",
      "West Virginia (WV)",
      "Wisconsin (WI)",
      "Wyoming (WY)",
    ],
    offerTypes: OFFER_TYPES_OPTIONS,
  });

  const [filters, setFilters] = useState({
    ...filterParams,
    showHeightDropdown: false,
    showWeightDropdown: false,
    showWingspanDropdown: false,
    showHandDropdown: false,
    trackittScoreType: filterParams.trackittScoreType || null,
    trackittScoreValue: filterParams.trackittScoreValue ?? "",
    positionScoreType: filterParams.positionScoreType || null,
    positionScoreValue: filterParams.positionScoreValue ?? "",
  });

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        setLoading((prev) => ({ ...prev, years: true }));
        const years = await fetchGraduationYears(apiInstance);
        setDropdownOptions((prev) => ({ ...prev, year: years }));

        setLoading((prev) => ({ ...prev, events: true }));
        const events = await fetchPublicEvents(apiInstance);
        const formattedEvents = events.map((event) => ({
          label: event.name,
          value: event.id,
        }));
        setDropdownOptions((prev) => ({ ...prev, eventIds: formattedEvents }));

        if (!isHighSchoolLeaderboard) {
          setLoading((prev) => ({ ...prev, schools: true }));
          const schools = await fetchHighSchool(apiInstance);
          setDropdownOptions((prev) => ({ ...prev, highSchools: schools }));
        }
      } catch (error) {
        console.error("Error fetching dropdown options:", error);
      } finally {
        setLoading({ years: false, events: false, schools: false });
      }
    };

    fetchDropdownData();
  }, [apiInstance, isHighSchoolLeaderboard]);

  useEffect(() => {
    if (dropdownOptions.eventIds.length > 0) {
      const updatedFilters = {
        ...filterParams,
        eventIds: (filterParams.eventIds || []).map((id) => {
          if (typeof id === "object") {
            return id;
          }
          const event = dropdownOptions.eventIds.find((e) => e.value === id);
          return event || { value: id, label: `Event ${id}` };
        }),
        position: (filterParams.position || []).map(toDisplay),
        trackittScoreType: filterParams.trackittScoreType || null,
        trackittScoreValue: filterParams.trackittScoreValue ?? "",
        positionScoreType: filterParams.positionScoreType || null,
        positionScoreValue: filterParams.positionScoreValue ?? "",
      };
      setFilters((prev) => ({ ...prev, ...updatedFilters }));
    }
  }, [filterParams, dropdownOptions.eventIds]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));

    if (
      (name === "trackittScoreType" || name === "trackittScoreValue") &&
      validationErrors.trackittScore
    ) {
      setValidationErrors((prev) => ({ ...prev, trackittScore: null }));
    }
    if (
      (name === "positionScoreType" || name === "positionScoreValue") &&
      validationErrors.positionScore
    ) {
      setValidationErrors((prev) => ({ ...prev, positionScore: null }));
    }
  };

  const handleHeightWeightChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const validateFilters = () => {
    const errors = {};

    if (filters.trackittScoreType) {
      const v = parseFloat(filters.trackittScoreValue);
      if (isNaN(v) || v <= 0) {
        errors.trackittScore = "Enter a Trackitt score ≥ 1";
      }
    }

    if (filters.positionScoreType) {
      const v = parseFloat(filters.positionScoreValue);
      if (isNaN(v) || v <= 0) {
        errors.positionScore = "Enter a Position score ≥ 1";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      year: [],
      position: [],
      eventIds: [],
      highSchools: [],
      states: [],
      heightFilterOperator: null,
      heightValue: "",
      weightFilterOperator: null,
      weightValue: "",
      wingspanFilterOperator: null,
      wingspanValue: "",
      handFilterOperator: null,
      handValue: "",
      scoreFilterTypes: [],
      scoreFilterOperator: null,
      scoreValue: "",
      showHeightDropdown: false,
      showWeightDropdown: false,
      showWingspanDropdown: false,
      showHandDropdown: false,
      trackittScoreType: null,
      trackittScoreValue: "",
      positionScoreType: null,
      positionScoreValue: "",
      offerTypes: [],
    };

    setFilters(clearedFilters);
    setValidationErrors({});
    setShowFilters(true);
    if (onClearFilters) onClearFilters();
  };

  const handleApplyFilters = () => {
    if (!validateFilters()) return;

    const payload = {
      ...filters,
      position: (filters.position || []).map(toApi),
      eventIds: filters.eventIds || [],
      trackittScoreType: filters.trackittScoreType,
      trackittScoreValue: filters.trackittScoreValue,
      positionScoreType: filters.positionScoreType,
      positionScoreValue: filters.positionScoreValue,
      includeGraduatedKids: includeGraduatedKids,
      offerTypes: filters.offerTypes || [],
    };

    onApplyFilters(payload);
    setShowFilters(false);
  };

  return (
    <div className="p-0">
      <button
        className="bg-gray-200 mb-2 border border-gray-300 rounded-md px-4 py-2 flex items-center gap-2 relative z-20"
        onClick={() => setShowFilters(!showFilters)}
      >
        <FontAwesomeIcon icon={showFilters ? faXmark : faFilter} />
        {showFilters ? "Close Filters" : "Filters"}
      </button>

      {showFilters && (
        <div
          className={`border border-gray-300 absolute left-0 md:left-8 mx-2 sm:mx-auto bg-white rounded-lg shadow space-y-4 z-20 w-10/12 ${
            isHighSchoolCoach ? "xl:left-80" : "xl:left-24"
          }`}
        >
          <h2 className="text-lg font-bold pl-4 pt-4">Filters</h2>
          <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-5 px-4 h-[550px] overflow-auto sm:h-auto sm:overflow-visible">
            <div>
              <DropdownWithSearch
                label="Year"
                options={dropdownOptions.year}
                selectedValues={filters.year || []}
                onSelect={(values) => handleFilterChange("year", values)}
                isLoading={loading.years}
              />
            </div>
            <div>
              {" "}
              <DropdownWithSearch
                label="Position"
                options={dropdownOptions.position}
                selectedValues={filters.position || []}
                onSelect={(values) => handleFilterChange("position", values)}
              />
            </div>
            <div>
              <DropdownWithSearch
                label="Events"
                options={dropdownOptions.eventIds}
                selectedValues={filters.eventIds || []}
                onSelect={(values) => handleFilterChange("eventIds", values)}
                isLoading={loading.events}
                isObjectArray={true}
              />
            </div>
            {!isHighSchoolLeaderboard && (
              <div>
                <DropdownWithSearch
                  label="School"
                  options={dropdownOptions.highSchools}
                  selectedValues={filters.highSchools || []}
                  onSelect={(values) =>
                    handleFilterChange("highSchools", values)
                  }
                  isLoading={loading.schools}
                />
              </div>
            )}
            <div>
              {" "}
              <DropdownWithSearch
                label="State"
                options={dropdownOptions.states}
                selectedValues={filters.states || []}
                onSelect={(values) => handleFilterChange("states", values)}
              />
            </div>
            <div>
              {" "}
              <ConditionDropdown
                label="Height"
                operator={filters.heightFilterOperator}
                value={filters.heightValue}
                showDropdown={filters.showHeightDropdown}
                onOperatorChange={(operator) =>
                  setFilters((prev) => ({
                    ...prev,
                    heightFilterOperator: operator,
                    showHeightDropdown: false,
                  }))
                }
                onValueChange={(newValue) =>
                  handleHeightWeightChange("heightValue", newValue)
                }
                onToggleDropdown={() =>
                  setFilters((prev) => ({
                    ...prev,
                    showHeightDropdown: !prev.showHeightDropdown,
                  }))
                }
                placeholder="0"
              />
            </div>
            <div>
              {" "}
              <ConditionDropdown
                label="Weight"
                operator={filters.weightFilterOperator}
                value={filters.weightValue}
                showDropdown={filters.showWeightDropdown}
                onOperatorChange={(operator) =>
                  setFilters((prev) => ({
                    ...prev,
                    weightFilterOperator: operator,
                    showWeightDropdown: false,
                  }))
                }
                onValueChange={(newValue) =>
                  handleHeightWeightChange("weightValue", newValue)
                }
                onToggleDropdown={() =>
                  setFilters((prev) => ({
                    ...prev,
                    showWeightDropdown: !prev.showWeightDropdown,
                  }))
                }
                placeholder="0"
              />
            </div>
            <div>
              {" "}
              <ConditionDropdown
                label="Wingspan"
                operator={filters.wingspanFilterOperator}
                value={filters.wingspanValue}
                showDropdown={filters.showWingspanDropdown}
                onOperatorChange={(operator) =>
                  setFilters((prev) => ({
                    ...prev,
                    wingspanFilterOperator: operator,
                    showWingspanDropdown: false,
                  }))
                }
                onValueChange={(newValue) =>
                  handleHeightWeightChange("wingspanValue", newValue)
                }
                onToggleDropdown={() =>
                  setFilters((prev) => ({
                    ...prev,
                    showWingspanDropdown: !prev.showWingspanDropdown,
                  }))
                }
                placeholder="0"
              />
            </div>
            <div>
              {" "}
              <ConditionDropdown
                label="Hand"
                operator={filters.handFilterOperator}
                value={filters.handValue}
                showDropdown={filters.showHandDropdown}
                onOperatorChange={(operator) =>
                  setFilters((prev) => ({
                    ...prev,
                    handFilterOperator: operator,
                    showHandDropdown: false,
                  }))
                }
                onValueChange={(newValue) =>
                  handleHeightWeightChange("handValue", newValue)
                }
                onToggleDropdown={() =>
                  setFilters((prev) => ({
                    ...prev,
                    showHandDropdown: !prev.showHandDropdown,
                  }))
                }
                placeholder="0"
              />
            </div>
            <div>
              {" "}
              <div className="w-full md:w-64 relative">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Trackit Scores
                </label>

                {/* Dropdown trigger */}
                <button
                  type="button"
                  onClick={() =>
                    setOpenDropdown(
                      openDropdown === "trackit" ? null : "trackit",
                    )
                  }
                  className={`w-full flex items-center justify-between border rounded px-4 py-2 bg-white text-left ${
                    validationErrors.trackittScore ? "border-red-500" : ""
                  }`}
                >
                  {filters.trackittScoreType === "TrackitScore" &&
                    "Overall Trackit Score"}
                  {filters.trackittScoreType === "TrackitAthleticScore" &&
                    "Trackit Athletic Score"}
                  {filters.trackittScoreType === "TrackitMeasurementScore" &&
                    "Trackit Measurement Score"}
                  {!filters.trackittScoreType && "Select score type"}
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`ml-2 transition-transform ${
                      openDropdown === "position" ? "rotate-0" : ""
                    }`}
                  />
                </button>

                {/* Dropdown list */}
                {openDropdown === "trackit" && (
                  <ul className="absolute z-10 mt-1 w-full bg-white border rounded shadow-md">
                    {[
                      { label: "Overall Trackit Score", value: "TrackitScore" },
                      {
                        label: "Trackit Athletic Score",
                        value: "TrackitAthleticScore",
                      },
                      {
                        label: "Trackit Measurement Score",
                        value: "TrackitMeasurementScore",
                      },
                    ].map((item) => (
                      <li
                        key={item.value}
                        className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                        onClick={() => {
                          handleFilterChange("trackittScoreType", item.value);
                          setOpenDropdown(null);
                        }}
                      >
                        {item.label}
                      </li>
                    ))}
                  </ul>
                )}

                <label className="block mt-2 mb-1 text-sm font-medium text-gray-700">
                  Score (greater than or equal)
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  className={`w-full border rounded px-4 py-2 text-gray-700 ${
                    validationErrors.trackittScore ? "border-red-500" : ""
                  }`}
                  value={filters.trackittScoreValue}
                  onChange={(e) =>
                    handleFilterChange("trackittScoreValue", e.target.value)
                  }
                  placeholder="0"
                />

                {validationErrors.trackittScore && (
                  <div className="flex items-center gap-2 text-red-600 text-sm mt-2">
                    <FontAwesomeIcon
                      icon={faExclamationTriangle}
                      className="w-3 h-3"
                    />
                    <span>{validationErrors.trackittScore}</span>
                  </div>
                )}
              </div>
            </div>
            <div>
              <div className="w-full md:w-64 relative">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Position Scores
                </label>

                {/* Dropdown trigger */}
                <button
                  type="button"
                  onClick={() =>
                    setOpenDropdown(
                      openDropdown === "position" ? null : "position",
                    )
                  }
                  className={`w-full flex items-center justify-between border rounded px-4 py-2 bg-white text-left ${
                    validationErrors.positionScore ? "border-red-500" : ""
                  }`}
                >
                  <span className="truncate">
                    {filters.positionScoreType === "PositionScore" &&
                      "Overall Position Score"}
                    {filters.positionScoreType === "PositionAthleticScore" &&
                      "Position Athletic Score"}
                    {filters.positionScoreType === "PositionMeasurementScore" &&
                      "Position Measurement Score"}
                    {!filters.positionScoreType && "Select score type"}
                  </span>

                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`ml-2 transition-transform ${
                      openDropdown === "position" ? "rotate-0" : ""
                    }`}
                  />
                </button>

                {/* Dropdown list */}
                {openDropdown === "position" && (
                  <ul className="absolute z-10 mt-1 w-full bg-white border rounded shadow-md">
                    {[
                      {
                        label: "Overall Position Score",
                        value: "PositionScore",
                      },
                      {
                        label: "Position Athletic Score",
                        value: "PositionAthleticScore",
                      },
                      {
                        label: "Position Measurement Score",
                        value: "PositionMeasurementScore",
                      },
                    ].map((item) => (
                      <li
                        key={item.value}
                        className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                        onClick={() => {
                          handleFilterChange("positionScoreType", item.value);
                          setOpenDropdown(null);
                        }}
                      >
                        {item.label}
                      </li>
                    ))}
                  </ul>
                )}

                <label className="block mt-2 mb-1 text-sm font-medium text-gray-700">
                  Score (greater than or equal)
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  className={`w-full border rounded px-4 py-2 text-gray-700 ${
                    validationErrors.positionScore ? "border-red-500" : ""
                  }`}
                  value={filters.positionScoreValue}
                  onChange={(e) =>
                    handleFilterChange("positionScoreValue", e.target.value)
                  }
                  placeholder="0"
                />

                {validationErrors.positionScore && (
                  <div className="flex items-center gap-2 text-red-600 text-sm mt-2">
                    <FontAwesomeIcon
                      icon={faExclamationTriangle}
                      className="w-3 h-3"
                    />
                    <span>{validationErrors.positionScore}</span>
                  </div>
                )}
              </div>
            </div>

            <div>
              {" "}
              <DropdownWithSearch
                label="Offer types"
                options={dropdownOptions.offerTypes}
                selectedValues={filters.offerTypes || []}
                onSelect={(values) => handleFilterChange("offerTypes", values)}
              />
            </div>
            <div>
              {" "}
              <div className="flex items-center gap-2 px-2 py-2 mt-6">
                <input
                  id="graduatedKids"
                  type="checkbox"
                  checked={includeGraduatedKids}
                  onChange={(e) => setIncludeGraduatedKids(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label
                  htmlFor="graduatedKids"
                  className="text-sm text-gray-700"
                >
                  Include graduated athletes
                </label>
              </div>
            </div>
          </div>
          <div className="bg-gray-100 p-4 rounded-b-lg">
            <div className="flex justify-end gap-4">
              <button
                className="px-6 py-2 border rounded bg-white"
                onClick={handleClearFilters}
              >
                Clear All
              </button>
              <button
                className="px-6 py-2 bg-red-500 text-white rounded"
                onClick={handleApplyFilters}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterPanel;
